import { PartItem } from '@cutr/constants/cutlist';
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useShallow } from 'zustand/react/shallow';

import { useActiveGroup } from '@/api/materialsGroup';
import { useCutlistState } from '@/api/store';

interface SelectedPartsContextType {
  selectedParts: PartItem[];
  togglePartSelection: (part: PartItem) => void;
  disableAll: () => void;
  enableAll: () => void;
}

const SelectedPartsContext = createContext<
  SelectedPartsContextType | undefined
>(undefined);

export const SelectedPartsProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [selectedParts, setSelectedParts] = useState<PartItem[]>([]);
  const activeGroup = useActiveGroup();
  const activeGroupParts = useCutlistState(
    useShallow((state) =>
      state.parts.filter((p) => p.groupId === activeGroup?.id)
    )
  );

  React.useEffect(() => {
    setSelectedParts(() => []);
  }, [activeGroup]);

  const togglePartSelection = useCallback((part: PartItem) => {
    setSelectedParts((prev) => {
      if (prev.find((p) => p.id === part.id)) {
        return prev.filter((p) => p.id !== part.id);
      }
      return [...prev, part];
    });
  }, []);

  const disableAll = useCallback(() => {
    setSelectedParts(() => []);
  }, []);

  const enableAll = useCallback(() => {
    setSelectedParts(() => activeGroupParts ?? []);
  }, [activeGroupParts]);

  const contextValue = useMemo(
    () => ({ selectedParts, togglePartSelection, disableAll, enableAll }),
    [selectedParts, togglePartSelection, disableAll, enableAll]
  );

  return (
    <SelectedPartsContext.Provider value={contextValue}>
      {children}
    </SelectedPartsContext.Provider>
  );
};

export const useSelectedPartsContext = () => {
  const context = useContext(SelectedPartsContext);
  if (context === undefined) {
    throw new Error(
      'useSelectedPartsContext must be used within a SelectedPartsProvider'
    );
  }
  return context;
};
